import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { User } from '../api/auth';
import {
  getPsynetHistory,
  publishByDateToPsynet,
} from '../api';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';
import { useAdminList } from '../hooks/useAdminList';

interface AdminPsynetTabProps {
  currentUser: User;
}

const AdminPsynetTab: React.FC<AdminPsynetTabProps> = ({ currentUser }) => {
  // Psynet 발행 관련 상태
  const [publishDate, setPublishDate] = useState('');
  const [publishSport, setPublishSport] = useState('');
  const [publishingByDate, setPublishingByDate] = useState(false);
  const [psynetAdminId, setPsynetAdminId] = useState<number | undefined>(currentUser?.admin_id);
  const [psynetFilters, setPsynetFilters] = useState({ sport: '', status: '' });
  const [psynetHistories, setPsynetHistories] = useState<any[]>([]);
  const [psynetPage, setPsynetPage] = useState(1);
  const [psynetPagination, setPsynetPagination] = useState({ total_pages: 0, total_count: 0 });
  const [loading, setLoading] = useState(false);

  // 관리자 목록 (마운트 시 자동 조회)
  const { adminList } = useAdminList();

  useEffect(() => {
    loadPsynetHistories(1);
  }, []);

  const loadPsynetHistories = async (page: number) => {
    setLoading(true);
    try {
      const response = await getPsynetHistory({
        page,
        page_size: 10,
        sport: psynetFilters.sport || undefined,
        status: psynetFilters.status || undefined
      });
      setPsynetHistories(response.data || []);
      setPsynetPage(page);
      setPsynetPagination({
        total_pages: response.pagination?.total_pages || 0,
        total_count: response.pagination?.total_count || 0
      });
    } catch (error) {
      console.error('Psynet 히스토리 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishByDate = async () => {
    if (!publishDate) {
      toast.error('날짜를 선택해.');
      return;
    }
    setPublishingByDate(true);
    try {
      const dateStr = publishDate.replace(/-/g, '');
      const result = await publishByDateToPsynet(dateStr, publishSport || undefined, psynetAdminId);
      const successCount = result.results?.filter((r: any) => r.status === 'success').length || 0;
      toast.success(`Psynet 발행 완료: ${successCount}건 발행됨 (전체 ${result.total || 0}건)`);
      loadPsynetHistories(1);
    } catch (error: any) {
      toast.error(`발행 실패: ${error.message}`);
    } finally {
      setPublishingByDate(false);
    }
  };

  return (
    <div style={styles.envContainer}>
      <div style={styles.envSection}>
        <h4 style={styles.envSectionTitle}>특정 날짜 일괄 발행</h4>
        <div style={styles.envFormRow}>
          {adminList.length > 0 && (
            <div style={styles.formGroup}>
              <label style={styles.label}>대상 관리자</label>
              <select
                style={styles.select}
                value={psynetAdminId || ''}
                onChange={(e) => setPsynetAdminId(e.target.value ? Number(e.target.value) : undefined)}
              >
                {adminList.map(admin => (
                  <option key={admin.admin_id} value={admin.admin_id}>
                    {admin.display_name} ({admin.role === 'administrator' ? '관리자' : '매니저'})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={styles.formGroup}>
            <label style={styles.label}>경기 날짜</label>
            <input
              type="date"
              style={styles.input}
              value={publishDate}
              onChange={(e) => setPublishDate(e.target.value)}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>종목</label>
            <select
              style={styles.select}
              value={publishSport}
              onChange={(e) => setPublishSport(e.target.value)}
            >
              <option value="">전체</option>
              <option value="soccer">축구</option>
              <option value="baseball">야구</option>
              <option value="basketball">농구</option>
              <option value="volleyball">배구</option>
              <option value="hockey">하키</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>&nbsp;</label>
            <button
              onClick={handlePublishByDate}
              disabled={!publishDate || publishingByDate}
              style={styles.addButton}
            >
              {publishingByDate ? '발행 중...' : '일괄 발행'}
            </button>
          </div>
        </div>
      </div>

      <div style={styles.envSection}>
        <h4 style={styles.envSectionTitle}>Psynet 발행 내역</h4>
        <div style={styles.envFormRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>종목</label>
            <select
              style={styles.select}
              value={psynetFilters.sport}
              onChange={(e) => setPsynetFilters({...psynetFilters, sport: e.target.value})}
            >
              <option value="">전체</option>
              <option value="soccer">축구</option>
              <option value="baseball">야구</option>
              <option value="basketball">농구</option>
              <option value="volleyball">배구</option>
              <option value="hockey">하키</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>상태</label>
            <select
              style={styles.select}
              value={psynetFilters.status}
              onChange={(e) => setPsynetFilters({...psynetFilters, status: e.target.value})}
            >
              <option value="">전체</option>
              <option value="success">성공</option>
              <option value="failed">실패</option>
              <option value="skipped">건너뜀</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>&nbsp;</label>
            <button
              onClick={() => loadPsynetHistories(1)}
              style={styles.addButton}
            >
              조회
            </button>
          </div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>로딩 중...</div>
        ) : psynetHistories.length === 0 ? (
          <div style={styles.emptyState}>발행 내역이 없음.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>경기일시</th>
                  <th style={styles.th}>종목</th>
                  <th style={styles.th}>리그</th>
                  <th style={styles.th}>홈팀</th>
                  <th style={styles.th}>원정팀</th>
                  <th style={styles.th}>상태</th>
                  <th style={styles.th}>발행시간</th>
                </tr>
              </thead>
              <tbody>
                {psynetHistories.map((history) => (
                  <tr key={history.history_id}>
                    <td style={styles.td}>
                      {history.match_info?.match_date?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')} {history.match_info?.match_time}
                    </td>
                    <td style={styles.td}>{history.match_info?.sport_name_kr || history.match_info?.sport_name}</td>
                    <td style={styles.td}>{history.match_info?.league_name}</td>
                    <td style={styles.td}>{history.match_info?.home_team}</td>
                    <td style={styles.td}>{history.match_info?.away_team}</td>
                    <td style={styles.td}>
                      <span style={history.publish_status === 'success' ? styles.statusActive : styles.statusInactive}>
                        {history.publish_status === 'success' ? '성공' : history.publish_status === 'failed' ? '실패' : '건너뜀'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {history.published_at ? new Date(history.published_at).toLocaleString('ko-KR') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {psynetPagination.total_pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: spacing[4], marginTop: spacing[4] }}>
                <button
                  style={styles.editButton}
                  onClick={() => loadPsynetHistories(psynetPage - 1)}
                  disabled={psynetPage === 1}
                >
                  이전
                </button>
                <span>{psynetPage} / {psynetPagination.total_pages} 페이지 (전체 {psynetPagination.total_count}개)</span>
                <button
                  style={styles.editButton}
                  onClick={() => loadPsynetHistories(psynetPage + 1)}
                  disabled={psynetPage >= psynetPagination.total_pages}
                >
                  다음
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  envContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[5],
  },
  envSection: {
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
    padding: spacing[6],
  },
  envSectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginTop: 0,
    marginBottom: spacing[5],
  },
  envFormRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[5],
    alignItems: 'flex-end',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  label: {
    ...commonStyles.label,
    minWidth: 'auto',
  },
  input: {
    ...commonStyles.input,
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    ...commonStyles.select,
    width: '100%',
  },
  addButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
  },
  editButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  tableContainer: {
    ...commonStyles.tableContainer,
  },
  table: {
    ...commonStyles.table,
  },
  th: {
    ...commonStyles.th,
  },
  td: {
    ...commonStyles.td,
  },
  emptyState: {
    ...commonStyles.emptyState,
  },
  statusActive: {
    color: colors.status.success,
    fontWeight: typography.fontWeight.medium,
  },
  statusInactive: {
    color: colors.status.error,
    fontWeight: typography.fontWeight.medium,
  },
};

export default AdminPsynetTab;
